import {
  createContext,
  use,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

/**
 * Keyboard shortcuts (DESIGN.md rule 3). Components declare shortcuts where they live; the ones
 * with a description show up in the `?` cheat-sheet automatically, so it's never out of date.
 *
 * Key syntax: "mod+k" (Ctrl on Windows/Linux, ⌘ on Mac), "ctrl+enter", "shift+?", "j", "escape".
 */
export interface ShortcutInfo {
  keys: string;
  description: string;
  group: string;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

function matches(e: KeyboardEvent, combo: string): boolean {
  const parts = combo.toLowerCase().split('+');
  const key = parts.pop()!;
  const want = new Set(parts);
  const mod = isMac ? e.metaKey : e.ctrlKey;
  if (want.has('mod') !== mod) return false;
  if (!want.has('mod') && want.has('ctrl') !== e.ctrlKey) return false;
  if (want.has('alt') !== e.altKey) return false;
  // "?" already implies shift on most layouts: don't require the modifier to be declared.
  if (key !== '?' && want.has('shift') !== e.shiftKey) return false;
  return e.key.toLowerCase() === key || (key === 'escape' && e.key === 'Escape');
}

const isTyping = (t: EventTarget | null) =>
  t instanceof HTMLElement && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));

/** Pretty label for a combo: "mod+k" → "Ctrl K" / "⌘ K". */
export function keyLabel(combo: string): string[] {
  if (combo === 'shift+?') return ['?'];
  return combo.split('+').map((k) => {
    const x = k.toLowerCase();
    if (x === 'mod') return isMac ? '⌘' : 'Ctrl';
    if (x === 'ctrl') return 'Ctrl';
    if (x === 'shift') return '⇧';
    if (x === 'alt') return isMac ? '⌥' : 'Alt';
    if (x === 'enter') return 'Enter';
    if (x === 'escape') return 'Esc';
    return k.length === 1 ? k.toUpperCase() : k;
  });
}

function createRegistry() {
  let items = new Map<string, ShortcutInfo>();
  let snapshot: ShortcutInfo[] = [];
  const listeners = new Set<() => void>();
  const emit = () => {
    snapshot = [...items.values()];
    for (const l of listeners) l();
  };
  return {
    add: (id: string, info: ShortcutInfo) => {
      items = new Map(items).set(id, info);
      emit();
    },
    remove: (id: string) => {
      items = new Map(items);
      items.delete(id);
      emit();
    },
    subscribe: (l: () => void) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    get: () => snapshot,
  };
}

const Registry = createContext<ReturnType<typeof createRegistry> | null>(null);

export function HotkeysProvider({ children }: { children: ReactNode }) {
  const [registry] = useState(createRegistry);
  return <Registry value={registry}>{children}</Registry>;
}

export function useRegisteredShortcuts(): ShortcutInfo[] {
  const r = use(Registry);
  return useSyncExternalStore(r?.subscribe ?? (() => () => {}), r?.get ?? (() => []));
}

interface Options {
  enabled?: boolean;
  /** Also fire while typing in an input (for mod+… and Escape combos). */
  allowInInputs?: boolean;
  /** Shown in the `?` cheat-sheet when set. */
  description?: string;
  group?: string;
}

export function useHotkey(
  keys: string,
  handler: (e: KeyboardEvent) => void,
  opts: Options = {},
): void {
  const { enabled = true, allowInInputs = false, description, group = 'General' } = opts;
  const ref = useRef(handler);
  useEffect(() => {
    ref.current = handler;
  });
  const registry = use(Registry);
  const id = useId();

  useEffect(() => {
    if (!enabled || !description || !registry) return undefined;
    registry.add(id, { keys, description, group });
    return () => registry.remove(id);
  }, [enabled, description, group, keys, id, registry]);

  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      if (!allowInInputs && isTyping(e.target)) return;
      if (!matches(e, keys)) return;
      e.preventDefault();
      ref.current(e);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [keys, enabled, allowInInputs]);
}
